import {FC, useEffect} from 'react'
import {useAuth} from '../../../auth'
import {
  OTHER_TAB_SWITCHED_EVENT,
  OtherTabSwitch,
  listenToOtherTabs,
  markTabInUse,
  serverKeepsBusinessPerTab,
  takeBusinessNotice,
} from '../../../auth/core/tabBusiness'
import {showToast} from '../toast/toast'
import {useCurrentBusiness} from './useCurrentBusiness'
import {staffPicksBusiness} from '../../../auth/core/staffAccess'

const TITLE_SEPARATOR = ' – '
let titlePrefix = ''

// Every copy, not only a leading one: the panel builds some titles from the one already shown.
const strip = (title: string, prefix: string) => (prefix ? title.split(prefix).join('') : title)

const withoutPrefix = (title: string) => strip(title, titlePrefix)

/** The browser tab's title led by the business, once: «پارس‌کالا – صورتحساب‌های فروش». */
export const prefixedTitle = (title: string, shortName: string) => {
  const prefix = shortName ? `${shortName}${TITLE_SEPARATOR}` : ''
  return prefix + strip(withoutPrefix(title), prefix)
}

/** «در تب دیگری «…» باز شد؛ این تب همچنان روی «…» است.» */
export const otherTabText = (change: OtherTabSwitch, current?: string) =>
  `در تب دیگری ${change.name ? `«${change.name}»` : 'کسب‌وکار دیگری'} باز شد؛ این تب همچنان روی ${
    current ? `«${current}»` : 'کسب‌وکار خودش'
  } است.`

/**
 * What a tab says about its business beyond the header chip (the user, 2026-10-26): for a user of more than one
 * business, whose tabs can differ, the business leads the browser tab's title; a message after the page reloaded into
 * another business; and a note when another tab of the browser opened a business, since this tab keeps its own. The
 * tab in use also becomes the one a newly opened tab starts from.
 */
export const BusinessTabNotices: FC<{
  /** False where no business is being worked in (the «کسب‌وکارهای من» page): the title stays the page's own. */
  businessInTitle?: boolean
}> = ({businessInTitle = true}) => {
  const {currentUser} = useAuth()
  const business = useCurrentBusiness()
  const manyBusinesses = staffPicksBusiness(currentUser) || (currentUser?.cpl?.length || 0) > 1
  const shortName = manyBusinesses && businessInTitle ? business?.shortName || '' : ''
  const display = business?.display

  // The message the reload left, once.
  useEffect(() => {
    const left = takeBusinessNotice()
    if (left) showToast('warning', left)
  }, [])

  useEffect(() => {
    const apply = () => {
      const next = prefixedTitle(document.title, shortName)
      if (next !== document.title) document.title = next
    }
    document.title = withoutPrefix(document.title)
    titlePrefix = shortName ? `${shortName}${TITLE_SEPARATOR}` : ''
    apply()
    const title = document.querySelector('title')
    const observer =
      typeof MutationObserver === 'undefined' ? undefined : new MutationObserver(apply)
    if (title && observer)
      observer.observe(title, {childList: true, characterData: true, subtree: true})
    return () => {
      observer?.disconnect()
      document.title = withoutPrefix(document.title)
      titlePrefix = ''
    }
  }, [shortName])

  useEffect(() => {
    listenToOtherTabs()
    const onOther = (event: Event) => {
      const change = (event as CustomEvent<OtherTabSwitch>).detail
      if (!serverKeepsBusinessPerTab() || !change) return
      if (change.userId && currentUser?.id && change.userId !== currentUser.id) return
      if (change.to === Number(currentUser?.cpi || 0)) return
      showToast('info', otherTabText(change, display))
    }
    const onUse = () => {
      if (document.visibilityState !== 'hidden') markTabInUse()
    }
    window.addEventListener(OTHER_TAB_SWITCHED_EVENT, onOther)
    window.addEventListener('focus', onUse)
    document.addEventListener('visibilitychange', onUse)
    onUse()
    return () => {
      window.removeEventListener(OTHER_TAB_SWITCHED_EVENT, onOther)
      window.removeEventListener('focus', onUse)
      document.removeEventListener('visibilitychange', onUse)
    }
  }, [currentUser?.id, currentUser?.cpi, display])

  return null
}
